const missing = (v: number | null | undefined): v is null | undefined => v === null || v === undefined;

// A figure the backend could not compute prints as a dash, never as zero.
export const eurOrDash = (v: number | null | undefined): string =>
  missing(v) ? "–" : `${v.toFixed(2)}€`;

export const eurPerGb = (v: number | null | undefined): string =>
  missing(v) ? "–" : `${v.toFixed(2)} €/GB`;

const symbolOf = (currency: string | null | undefined): string => {
  if (!currency || currency === "EUR") return "€";
  return currency === "USD" ? "$" : ` ${currency}`;
};

// A price in its own currency: "9.00€", "19.00$", "12.00 CHF"; a dash when there is none.
export const money = (v: number | null | undefined, currency: string | null | undefined): string =>
  missing(v) ? "–" : `${v.toFixed(2)}${symbolOf(currency)}`;

// A competitor figure that could not be converted is shown in its own currency and compared as it
// stands; the owner chose that over hiding the competitor (2026-10-14).
export const isFaceValue = (currency: string | null | undefined): boolean => !!currency && currency !== "EUR";

export const faceValueNote = (currency: string | null | undefined): string =>
  isFaceValue(currency) ? ` (në ${currency}, pa konvertim)` : "";

// Template names carry the environment they were made for ("Oman 50GB 30days - production"); it says
// nothing to a reader of these pages, so it is dropped from display only. The name itself is untouched.
export const packageLabel = (name: string | null | undefined): string | null =>
  name === null || name === undefined ? null : name.replace(/\s*-\s*(production|development)\s*$/i, "");

export const gb = (v: number | null | undefined): string =>
  missing(v) ? "–" : `${Number(v.toFixed(2))} GB`;

// A price gap against the cheapest competitor. Positive: we are dearer. Negative: we are cheaper.
// Within ten percent either way it is a wash, and shows as such.
export const gapTextClass = (gap: number | null | undefined): string => {
  if (missing(gap)) return "text-neutral";
  if (gap > 10) return "text-red font-medium";
  if (gap < -10) return "text-confirm font-medium";
  return "text-[#F59E0B] font-medium";
};

// "≈" marks a gap measured against a figure in another currency, taken at face value.
export const gapLabel = (gap: number | null | undefined, currency?: string | null): string =>
  missing(gap) ? "–" : `${isFaceValue(currency) ? "≈ " : ""}${gap > 0 ? "+" : ""}${Math.round(gap)}%`;

// A 404 on these endpoints means no snapshot exists yet (or none on or before the day asked for).
export const isNotFound = (error: unknown): boolean =>
  (error as { response?: { status?: number } } | null)?.response?.status === 404;
